import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./stylesForm.module.css";
import { BANCOS_OPTIONS, MOCK_FORM_PREENCHIMENTO } from "../mockParceirosAssociados";
import { usePermissoes } from "../../../config/Context/PermissoesContext";
import {
  getHierarquia,
  getResponsaveisComerciais,
  getAssociado,
  getParceiro,
  criarAssociado,
  criarParceiro,
  atualizarAssociado,
  atualizarParceiro,
} from "../../../services/api-associados-parceiros.service";
import {
  maskCpf,
  maskCnpj,
  maskPhone,
  maskCep,
  maskRg,
  formatDocumento,
  sanitizeNumero,
  sanitizeAgencia,
  sanitizeConta,
  sanitizeEmail,
  sanitizeNome,
  sanitizeRazaoSocial,
  sanitizePix,
  sanitizePassaporteRne,
} from "./formSanitization";
import BadgeIcon from "@mui/icons-material/Badge";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PlaceIcon from "@mui/icons-material/Place";
import PhoneIcon from "@mui/icons-material/Phone";
import GroupIcon from "@mui/icons-material/Group";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import ListIcon from "@mui/icons-material/List";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ScienceIcon from "@mui/icons-material/Science";
import SearchIcon from "@mui/icons-material/Search";
import { consultarCNPJUsuario } from "../../../services/auth.service";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const INITIAL_SOCIOS = [{ cpf: "", nome: "", email: "", telefone: "", respLegal: false, respFinanceiro: false }];

function parseIdParam(id) {
  if (!id || id === "novo") return { entityType: null, idForApi: null };
  const match = id.match(/^(associado|parceiro)-(\d+)$/);
  if (match) return { entityType: match[1], idForApi: parseInt(match[2], 10) };
  return { entityType: null, idForApi: null };
}

/** Persiste CEP no texto do endereço para edição posterior (prefixo "CEP: 00000-000"). */
function buildEndereco(cepRaw, logradouro, numero, bairro, cidade, uf) {
  const cepDigits = String(cepRaw || "").replace(/\D/g, "");
  const cepMasked = cepDigits.length === 8 ? maskCep(cepDigits) : "";
  const parts = [logradouro, numero].filter(Boolean);
  const part2 = [bairro, cidade, uf].filter(Boolean).join(", ");
  const rest = [...parts, part2].filter(Boolean).join(" - ");
  if (!cepMasked && !rest) return null;
  if (cepMasked && rest) return `CEP: ${cepMasked} - ${rest}`;
  return cepMasked ? `CEP: ${cepMasked}` : rest || null;
}

function parseEnderecoFromApi(str) {
  if (!str || typeof str !== "string") {
    return { cep: "", logradouro: "", numero: "", bairro: "", cidade: "", uf: "" };
  }
  const parts = str.split(" - ");
  let start = 0;
  let cep = "";
  const head = (parts[0] || "").trim();
  if (/^CEP:/i.test(head)) {
    cep = head.replace(/^CEP:\s*/i, "").trim();
    start = 1;
  }
  const logradouro = parts[start] || "";
  const numero = parts[start + 1] || "";
  const tail = parts[start + 2] || "";
  const bcu = tail.split(", ").map((x) => String(x).trim());
  return {
    cep: cep ? maskCep(cep.replace(/\D/g, "")) : "",
    logradouro,
    numero,
    bairro: bcu[0] || "",
    cidade: bcu[1] || "",
    uf: (bcu[2] || "").slice(0, 2),
  };
}

function buildDadosFinanceirosPayload(banco, bancoOutro, agencia, conta, pix, respCpf, respNome, respEmail, respTel) {
  const bancoVal = banco === "other" ? (bancoOutro || "") : (banco || "");
  const agenciaVal = agencia || "";
  const contaVal = conta || "";
  const pixVal = pix || "";
  const cpfDigits = String(respCpf || "").replace(/\D/g, "");
  const nomeVal = String(respNome || "").trim();
  const emailVal = String(respEmail || "").trim();
  const telDigits = String(respTel || "").replace(/\D/g, "");
  const hasAny =
    bancoVal ||
    agenciaVal ||
    contaVal ||
    pixVal ||
    cpfDigits.length === 11 ||
    nomeVal ||
    emailVal ||
    telDigits.length >= 10;
  if (!hasAny) return null;
  return {
    banco: bancoVal || "",
    agencia: agenciaVal,
    conta: contaVal,
    chave_pix: pixVal,
    resp_financeiro_cpf: cpfDigits.length === 11 ? cpfDigits : null,
    resp_financeiro_nome: nomeVal || null,
    resp_financeiro_email: emailVal || null,
    resp_financeiro_telefone: telDigits.length >= 10 ? String(respTel || "").trim() : null,
  };
}

export default function ParceirosAssociadosForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { entityType, idForApi } = parseIdParam(id);
  const { superAdmin } = usePermissoes();
  const isEdit = Boolean(entityType && idForApi);

  const [associadosOpcoes, setAssociadosOpcoes] = useState([]);
  const [responsaveisOpcoes, setResponsaveisOpcoes] = useState([]);
  const [loadingForm, setLoadingForm] = useState(!!isEdit);
  const [saving, setSaving] = useState(false);
  const [loadingCnpjConsulta, setLoadingCnpjConsulta] = useState(false);

  const refDocumento = useRef(null);
  const refNome = useRef(null);
  const refDataExpiracao = useRef(null);
  const refCep = useRef(null);
  const refNumero = useRef(null);
  const refTipoVinculo = useRef(null);
  const refAssociado = useRef(null);
  const refResponsavel = useRef(null);
  const refEmail = useRef(null);
  const refTelefone = useRef(null);
  const refValidationAlert = useRef(null);

  const [docTypeCnpj, setDocTypeCnpj] = useState(false);
  const [documento, setDocumento] = useState("");
  const [nome, setNome] = useState("");
  const [dataExpiracao, setDataExpiracao] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [cep, setCep] = useState("");
  const [cepLoading, setCepLoading] = useState(false);
  const [logradouro, setLogradouro] = useState("");
  const [numero, setNumero] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [uf, setUf] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [whatsapp, setWhatsapp] = useState(false);
  const [tipoVinculo, setTipoVinculo] = useState("");
  const [associadoVinculado, setAssociadoVinculado] = useState("");
  const [responsavelComercial, setResponsavelComercial] = useState("");
  const [rg, setRg] = useState("");
  const [estrangeiro, setEstrangeiro] = useState(false);
  const [passaporte, setPassaporte] = useState("");
  const [rne, setRne] = useState("");
  const [socios, setSocios] = useState(INITIAL_SOCIOS);
  const [respFinanceiroCpf, setRespFinanceiroCpf] = useState("");
  const [respFinanceiroNome, setRespFinanceiroNome] = useState("");
  const [respFinanceiroEmail, setRespFinanceiroEmail] = useState("");
  const [respFinanceiroTelefone, setRespFinanceiroTelefone] = useState("");
  const [banco, setBanco] = useState("");
  const [bancoOutro, setBancoOutro] = useState("");
  const [agencia, setAgencia] = useState("");
  const [conta, setConta] = useState("");
  const [pix, setPix] = useState("");
  const [cepMessage, setCepMessage] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [savedName, setSavedName] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    Promise.all([
      getHierarquia().then((res) => res?.data || []),
      getResponsaveisComerciais({ status: "ativo" }).then((res) => res?.data || []),
    ]).then(([hierarquia, responsaveis]) => {
      const assocList = (hierarquia || []).map((a) => ({
        value: String(a.id),
        label: a.razao_social,
        tipo: a.tipo,
        responsavelId: a.responsavel?.id,
        responsavelNome: a.responsavel?.nome,
      }));
      setAssociadosOpcoes(assocList);
      setResponsaveisOpcoes((responsaveis || []).map((r) => ({ value: String(r.id), label: r.nome })));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit || !entityType || !idForApi) {
      if (isEdit) setLoadingForm(false);
      return;
    }
    setLoadingForm(true);
    const req = entityType === "associado" ? getAssociado(idForApi) : getParceiro(idForApi);
    req
      .then((res) => {
        const d = res?.data || res;
        if (!d) return;
        const tipoDoc = (d.tipo_documento || "").toLowerCase();
        setDocTypeCnpj(tipoDoc === "cnpj");
        setDocumento(d.documento_formatado || d.documento || "");
        setNome(d.razao_social || "");
        setDataExpiracao(d.data_expiracao ? d.data_expiracao.slice(0, 10) : "");
        setAtivo(d.ativo !== false);
        setEmail(d.email || "");
        setTelefone(d.telefone || "");
        setTipoVinculo(entityType === "parceiro" ? "parceiro" : "associado");
        if (entityType === "parceiro" && d.associado?.id) setAssociadoVinculado(String(d.associado.id));
        const respId = d.responsavel_comercial?.id;
        if (respId) setResponsavelComercial(String(respId));
        if (d.endereco) {
          const p = parseEnderecoFromApi(d.endereco);
          setCep(p.cep);
          setLogradouro(p.logradouro);
          setNumero(p.numero);
          setBairro(p.bairro);
          setCidade(p.cidade);
          setUf(p.uf);
        }
        if (d.dados_cpf) {
          setRg(d.dados_cpf.rg || "");
          setEstrangeiro(!!d.dados_cpf.estrangeiro);
          setPassaporte(d.dados_cpf.passaporte || "");
          setRne(d.dados_cpf.rne || "");
        }
        if (d.socios?.length) {
          setSocios(
            d.socios.map((s) => ({
              cpf: s.cpf_formatado || s.cpf,
              nome: s.nome_completo,
              email: s.email || "",
              telefone: s.telefone ? maskPhone(String(s.telefone)) : "",
              respLegal: !!s.resp_legal,
              respFinanceiro: !!s.resp_financeiro,
            }))
          );
        }
        if (d.dados_financeiros) {
          setBanco(d.dados_financeiros.banco || "");
          setAgencia(d.dados_financeiros.agencia || "");
          setConta(d.dados_financeiros.conta || "");
          setPix(d.dados_financeiros.chave_pix || "");
          setRespFinanceiroCpf(d.dados_financeiros.resp_financeiro_cpf ? maskCpf(String(d.dados_financeiros.resp_financeiro_cpf)) : "");
          setRespFinanceiroNome(d.dados_financeiros.resp_financeiro_nome || "");
          setRespFinanceiroEmail(d.dados_financeiros.resp_financeiro_email || "");
          setRespFinanceiroTelefone(d.dados_financeiros.resp_financeiro_telefone ? maskPhone(String(d.dados_financeiros.resp_financeiro_telefone)) : "");
        }
      })
      .catch(() => {})
      .finally(() => setLoadingForm(false));
  }, [isEdit, entityType, idForApi]);

  useEffect(() => {
    if (tipoVinculo !== "parceiro" || !associadoVinculado) return;
    const assoc = associadosOpcoes.find((o) => o.value === associadoVinculado);
    if (assoc && assoc.tipo !== "INTERNO" && assoc.responsavelId) setResponsavelComercial(String(assoc.responsavelId));
  }, [tipoVinculo, associadoVinculado, associadosOpcoes]);

  const handleDocumentoChange = (e) => {
    setDocumento(formatDocumento(e.target.value, docTypeCnpj));
  };

  const resetForm = () => {
    setDocTypeCnpj(false);
    setDocumento("");
    setNome("");
    setDataExpiracao("");
    setAtivo(true);
    setCep("");
    setLogradouro("");
    setNumero("");
    setBairro("");
    setCidade("");
    setUf("");
    setEmail("");
    setTelefone("");
    setWhatsapp(false);
    setTipoVinculo("");
    setAssociadoVinculado("");
    setResponsavelComercial("");
    setRg("");
    setEstrangeiro(false);
    setPassaporte("");
    setRne("");
    setSocios(INITIAL_SOCIOS);
    setRespFinanceiroCpf("");
    setRespFinanceiroNome("");
    setRespFinanceiroEmail("");
    setRespFinanceiroTelefone("");
    setBanco("");
    setBancoOutro("");
    setAgencia("");
    setConta("");
    setPix("");
    setValidationErrors([]);
  };

  const fillMock = () => {
    const m = MOCK_FORM_PREENCHIMENTO;
    setDocTypeCnpj(m.docTypeCnpj);
    setDocumento(m.documento);
    setNome(m.nome);
    setDataExpiracao(m.dataExpiracao);
    setAtivo(m.ativo);
    setCep(m.cep);
    setLogradouro(m.logradouro);
    setNumero(m.numero);
    setBairro(m.bairro);
    setCidade(m.cidade);
    setUf(m.uf);
    setEmail(m.email);
    setTelefone(m.telefone);
    setWhatsapp(m.whatsapp);
    setTipoVinculo(m.tipoVinculo);
    setAssociadoVinculado(m.associadoVinculado);
    setResponsavelComercial(m.responsavelComercial);
    setRg(m.rg);
    setEstrangeiro(m.estrangeiro);
    setPassaporte(m.passaporte);
    setRne(m.rne);
    setSocios(m.socios.map((s) => ({ ...s })));
    setRespFinanceiroCpf(m.respFinanceiroCpf);
    setRespFinanceiroNome(m.respFinanceiroNome);
    setRespFinanceiroEmail(m.respFinanceiroEmail);
    setRespFinanceiroTelefone(m.respFinanceiroTelefone);
    setBanco(m.banco);
    setAgencia(m.agencia);
    setConta(m.conta);
    setPix(m.pix);
    setValidationErrors([]);
  };


  const buscarCep = async () => {
    const cepLimpo = cep.replace(/\D/g, "");
    if (cepLimpo.length !== 8) return;
    setCepLoading(true);
    setCepMessage(null);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`, { signal: controller.signal });
      const data = await res.json();
      if (data.erro) {
        setCepMessage({ text: "CEP não encontrado", color: "#d32f2f" });
        setTimeout(() => setCepMessage(null), 3000);
      } else {
        setLogradouro(data.logradouro || "");
        setBairro(data.bairro || "");
        setCidade(data.localidade || "");
        setUf(data.uf || "");
        setCepMessage({ text: "Endereço encontrado!", color: "#2e7d32" });
        setTimeout(() => setCepMessage(null), 2000);
        setTimeout(() => refNumero.current?.focus(), 50);
      }
    } catch (err) {
      const isAbort = err.name === "AbortError";
      setCepMessage({
        text: isAbort ? "Tempo esgotado. Tente novamente." : "Erro ao buscar CEP. Tente novamente.",
        color: "#d32f2f",
      });
      setTimeout(() => setCepMessage(null), 3000);
      if (!isAbort) console.error(err);
    } finally {
      clearTimeout(timeout);
      setCepLoading(false);
    }
  };

  const buscarCnpjaForm = async () => {
    const cnpjLimpo = documento.replace(/\D/g, "");
    if (cnpjLimpo.length !== 14) return;
    setLoadingCnpjConsulta(true);
    try {
      const res = await consultarCNPJUsuario(cnpjLimpo);
      const data = res?.dados ?? res;
      const razao = String(data.razao_social || data.nome || "").trim();
      if (razao) setNome(razao);
      const emailApi = String(data.email || "").trim();
      if (emailApi && !email) setEmail(emailApi);
      const telDigits = String(data.telefone1 || "").replace(/\D/g, "").slice(0, 11);
      if (telDigits && !telefone) setTelefone(maskPhone(telDigits));
      const cepDigits = String(data.cep || "").replace(/\D/g, "");
      if (cepDigits.length === 8) setCep(maskCep(cepDigits));
      if (data.logradouro) setLogradouro(String(data.logradouro));
      if (data.numero) setNumero(String(data.numero));
      if (data.bairro) setBairro(String(data.bairro));
      if (data.municipio) setCidade(String(data.municipio));
      if (data.uf) setUf(String(data.uf).slice(0, 2).toUpperCase());
      if (Array.isArray(data.qsa) && data.qsa.length > 0) {
        setSocios(data.qsa.map((s) => ({
          cpf: "",
          nome: String(s.nome || "").trim(),
          email: "",
          telefone: "",
          respLegal: false,
          respFinanceiro: false,
        })));
      }
    } catch {
      // falha silenciosa — campos permanecem editáveis
    } finally {
      setLoadingCnpjConsulta(false);
    }
  };

  const assocSelecionado = associadosOpcoes.find((o) => o.value === associadoVinculado);
  const ehAssociadoInterno = assocSelecionado?.tipo === "INTERNO";

  // Tipo vazio → aguarda; parceiro sem associado → aguarda; parceiro + não-INTERNO COM responsável definido → herdado/bloqueado
  // Se o associado não tem responsável definido, permite editar manualmente
  const responsavelDisabled =
    !tipoVinculo ||
    (tipoVinculo === "parceiro" && !associadoVinculado) ||
    (tipoVinculo === "parceiro" && !!associadoVinculado && !ehAssociadoInterno && !!assocSelecionado?.responsavelId);

  const responsavelOptions = (() => {
    if (tipoVinculo === "associado") return responsaveisOpcoes;
    if (ehAssociadoInterno) return responsaveisOpcoes;
    if (assocSelecionado?.responsavelId) {
      const nomeReal =
        responsaveisOpcoes.find((r) => r.value === String(assocSelecionado.responsavelId))?.label ??
        assocSelecionado.responsavelNome ??
        "Responsável do associado";
      return [{ value: String(assocSelecionado.responsavelId), label: nomeReal }];
    }
    return responsaveisOpcoes;
  })();

  const responsavelPlaceholder = (() => {
    if (!tipoVinculo) return "Selecione o tipo primeiro...";
    if (tipoVinculo === "parceiro" && !associadoVinculado) return "Selecione o associado primeiro...";
    if (responsavelDisabled) return "Definido pelo associado vinculado";
    return "Selecione...";
  })();

  const responsavelHelperText = (() => {
    if (!tipoVinculo) return "Selecione o tipo primeiro...";
    if (tipoVinculo === "parceiro" && !associadoVinculado) return "Selecione o associado primeiro...";
    if (tipoVinculo === "parceiro" && !!associadoVinculado && !ehAssociadoInterno) {
      return "Campo automático - Definido pelo Associado vinculado";
    }
    return null;
  })();

  const addSocio = () =>
    setSocios((s) => [
      ...s,
      { cpf: "", nome: "", email: "", telefone: "", respLegal: false, respFinanceiro: false },
    ]);
  const removeSocio = (idx) => {
    if (socios.length <= 1) return;
    setSocios((s) => s.filter((_, i) => i !== idx));
  };
  const updateSocio = (idx, field, value) => {
    setSocios((s) => s.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };

  const validate = () => {
    const errors = [];
    if (!documento.trim()) errors.push({ label: "Documento (CPF/CNPJ)", ref: refDocumento });
    if (!nome.trim()) errors.push({ label: "Nome / Razão Social", ref: refNome });
    if (!dataExpiracao) errors.push({ label: "Data de Expiração", ref: refDataExpiracao });
    if (!cep.trim()) errors.push({ label: "CEP", ref: refCep });
    if (!numero.trim()) errors.push({ label: "Número (endereço)", ref: refNumero });
    if (!tipoVinculo) errors.push({ label: "Tipo (Parceiro/Associado)", ref: refTipoVinculo });
    if (tipoVinculo === "parceiro" && !associadoVinculado) errors.push({ label: "Associado Vinculado", ref: refAssociado });
    if (!responsavelDisabled && !responsavelComercial && responsaveisOpcoes.length > 0) errors.push({ label: "Responsável Comercial", ref: refResponsavel });
    if (!email.trim()) errors.push({ label: "E-mail", ref: refEmail });
    if (!telefone.trim()) errors.push({ label: "Telefone", ref: refTelefone });
    return errors;
  };

  const handleSave = async () => {
    const errors = validate();
    if (errors.length > 0) {
      setValidationErrors(errors);
      const first = errors[0];
      setTimeout(() => {
        first.ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        first.ref.current?.focus?.();
      }, 100);
      return;
    }
    setValidationErrors([]);
    setSaving(true);
    const docDigits = documento.replace(/\D/g, "");
    const endereco = buildEndereco(cep, logradouro, numero, bairro, cidade, uf);
    const sociosPayload = docTypeCnpj
      ? socios
          .filter((s) => (s.cpf || "").replace(/\D/g, "").length === 11)
          .map((s) => ({
            cpf: s.cpf.replace(/\D/g, ""),
            nome_completo: s.nome || "",
            email: (s.email || "").trim() || null,
            telefone: (s.telefone || "").replace(/\D/g, "") || null,
            resp_legal: !!s.respLegal,
            resp_financeiro: !!s.respFinanceiro,
          }))
      : undefined;
    const dadosFinanceirosPayload = buildDadosFinanceirosPayload(
      banco,
      bancoOutro,
      agencia,
      conta,
      pix,
      respFinanceiroCpf,
      respFinanceiroNome,
      respFinanceiroEmail,
      respFinanceiroTelefone
    );
    try {
      if (tipoVinculo === "associado") {
        const base = {
          razao_social: nome,
          nome_fantasia: nome.trim() || null,
          email: email.trim(),
          telefone: telefone.trim() || null,
          endereco,
          data_expiracao: dataExpiracao,
          responsavel_comercial_id: responsavelComercial ? Number(responsavelComercial) : null,
          ativo,
          dados_cpf: !docTypeCnpj ? { rg: rg || null, estrangeiro, passaporte: passaporte || null, rne: rne || null } : null,
          dados_cnpj: docTypeCnpj ? {} : null,
          dados_financeiros: dadosFinanceirosPayload,
        };
        if (docTypeCnpj) base.socios = sociosPayload;
        if (isEdit) await atualizarAssociado(idForApi, base);
        else await criarAssociado({ tipo: "ASSOCIADO", tipo_documento: docTypeCnpj ? "CNPJ" : "CPF", documento: docDigits, ...base });
      } else {
        const payload = {
          associado_id: Number(associadoVinculado),
          tipo_documento: docTypeCnpj ? "CNPJ" : "CPF",
          documento: docDigits,
          razao_social: nome,
          email: email.trim(),
          telefone: telefone.trim() || null,
          endereco,
          data_expiracao: dataExpiracao,
          ativo,
          responsavel_comercial_id: ehAssociadoInterno && responsavelComercial ? Number(responsavelComercial) : null,
          dados_cpf: !docTypeCnpj ? { rg: rg || null, estrangeiro, passaporte: passaporte || null, rne: rne || null } : null,
          dados_financeiros: dadosFinanceirosPayload,
        };
        if (docTypeCnpj) payload.socios = sociosPayload;
        if (isEdit) await atualizarParceiro(idForApi, payload);
        else await criarParceiro(payload);
      }
      setSavedName(nome);
      setSuccessModalOpen(true);
    } catch (err) {
      const data = err?.response?.data;
      const msg = data?.message || data?.mensagem || err?.message || "Erro ao salvar";
      setValidationErrors([{ label: msg, ref: refNome }]);
      setTimeout(() => refValidationAlert.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.formContainer}>
      <nav className={styles.breadcrumb}>
        <a className={styles.breadcrumbLink} href="#/" onClick={(e) => { e.preventDefault(); navigate("/acessos/parceiros-associados"); }}>
          Parceiros e Associados
        </a>
        <span className={styles.breadcrumbSep}>›</span>
        <span>{isEdit ? "Editar" : "Novo Cadastro"}</span>
      </nav>

      <main className={styles.mainContent}>
        {loadingForm && (
          <div style={{ padding: 16, textAlign: "center", color: "#666" }}>Carregando dados...</div>
        )}
        <div className={styles.pageTitleSection}>
          <h1 className={styles.pageTitle}>
            <PersonIcon className={styles.pageTitleIcon} />
            {isEdit ? "Editar Parceiro / Associado" : "Novo Parceiro / Associado"}
          </h1>
        </div>

        <div style={loadingForm ? { opacity: 0.6, pointerEvents: "none" } : undefined}>
        {validationErrors.length > 0 && (
          <div
            ref={refValidationAlert}
            role="alert"
            className={styles.validationErrors}
            style={{
              marginBottom: 16,
              padding: "12px 16px",
              background: "#fce8e6",
              border: "1px solid #ea4335",
              borderRadius: 8,
              color: "#c5221f",
              fontSize: 14,
            }}
          >
            <strong>{validationErrors.length === 1 && validationErrors[0].ref === refNome ? "Erro ao salvar:" : "Campos obrigatórios não preenchidos:"}</strong>
            <ul style={{ margin: "8px 0 0 20px", padding: 0 }}>
              {validationErrors.map((e, i) => (
                <li key={i}>{e.label}</li>
              ))}
            </ul>
            {!(validationErrors.length === 1 && validationErrors[0].ref === refNome) && (
              <span style={{ fontSize: 12, display: "block", marginTop: 6 }}>O primeiro campo foi destacado. Preencha e tente salvar novamente.</span>
            )}
          </div>
        )}

        {/* Dados Básicos */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <BadgeIcon className={styles.cardHeaderIcon} />
            Dados Básicos
          </div>
          <div className={styles.cardBody}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className="required">Tipo de Documento</label>
                <div className={styles.documentTypeContainer}>
                  <span className={`${styles.documentTypeLabel} ${!docTypeCnpj ? styles.documentTypeLabelActive : ""}`}>CPF</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={docTypeCnpj} onChange={(e) => { setDocTypeCnpj(e.target.checked); setDocumento(""); }} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={`${styles.documentTypeLabel} ${docTypeCnpj ? styles.documentTypeLabelActive : ""}`}>CNPJ</span>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className="required">{docTypeCnpj ? "CNPJ" : "CPF"}</label>
                {docTypeCnpj ? (
                  <div className={styles.inputWithAction}>
                    <input
                      ref={refDocumento}
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={documento}
                      onChange={handleDocumentoChange}
                      placeholder="00.000.000/0000-00"
                      maxLength={18}
                      aria-invalid={validationErrors.some((e) => e.ref === refDocumento)}
                    />
                    <button
                      type="button"
                      className={styles.btnCnpj}
                      onClick={buscarCnpjaForm}
                      disabled={documento.replace(/\D/g, "").length !== 14 || loadingCnpjConsulta}
                      title="Consultar dados na Receita Federal (CNPJá)"
                    >
                      {loadingCnpjConsulta
                        ? <span className={styles.spinnerSmall} />
                        : <SearchIcon style={{ fontSize: 18 }} />}
                    </button>
                  </div>
                ) : (
                  <input
                    ref={refDocumento}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={documento}
                    onChange={handleDocumentoChange}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    aria-invalid={validationErrors.some((e) => e.ref === refDocumento)}
                  />
                )}
              </div>
              <div className={`${styles.formGroup} ${styles.formGroupSpan2}`}>
                <label className="required">{docTypeCnpj ? "Razão Social" : "Nome Completo"}</label>
                <input
                  ref={refNome}
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(docTypeCnpj ? sanitizeRazaoSocial(e.target.value) : sanitizeNome(e.target.value))}
                  placeholder="Digite o nome completo"
                  aria-invalid={validationErrors.some((e) => e.ref === refNome)}
                />
              </div>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className="required">Data de Expiração</label>
                <input ref={refDataExpiracao} type="date" value={dataExpiracao} onChange={(e) => setDataExpiracao(e.target.value)} aria-invalid={validationErrors.some((e) => e.ref === refDataExpiracao)} />
              </div>
              <div className={styles.formGroup}>
                <div className={styles.toggleContainer}>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={styles.toggleLabel}>Ativo</span>
                </div>
              </div>
            </div>

            <div className={styles.sectionTitle}>
              <PlaceIcon className={styles.sectionTitleIcon} />
              Endereço
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className="required">CEP</label>
                <input
                  ref={refCep}
                  type="text"
                  inputMode="numeric"
                  value={cep}
                  onChange={(e) => setCep(maskCep(e.target.value))}
                  onBlur={buscarCep}
                  placeholder="00000-000"
                  maxLength={9}
                  aria-invalid={validationErrors.some((e) => e.ref === refCep)}
                />
                {cepLoading && <span className={styles.formHelper} style={{ color: "#1a73e8" }}>Buscando endereço...</span>}
                {!cepLoading && cepMessage && (
                  <span className={styles.formHelper} style={{ color: cepMessage.color }}>{cepMessage.text}</span>
                )}
              </div>
              <div className={`${styles.formGroup} ${styles.formGroupSpan2}`}>
                <label>Logradouro</label>
                <input type="text" value={logradouro} readOnly placeholder="Rua, Avenida..." aria-readonly />
              </div>
              <div className={styles.formGroup}>
                <label className="required">Número</label>
                <input
                  ref={refNumero}
                  type="text"
                  inputMode="numeric"
                  value={numero}
                  onChange={(e) => setNumero(sanitizeNumero(e.target.value))}
                  placeholder="Somente números"
                  aria-invalid={validationErrors.some((e) => e.ref === refNumero)}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Bairro</label>
                <input type="text" value={bairro} readOnly />
              </div>
              <div className={`${styles.formGroup} ${styles.formGroupSpan2}`}>
                <label>Cidade</label>
                <input type="text" value={cidade} readOnly />
              </div>
              <div className={styles.formGroup}>
                <label>UF</label>
                <input type="text" value={uf} maxLength={2} readOnly />
              </div>
            </div>

            <div className={styles.sectionTitle}>
              <PhoneIcon className={styles.sectionTitleIcon} />
              Contato
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className="required">E-mail</label>
                <input
                  ref={refEmail}
                  type="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(sanitizeEmail(e.target.value))}
                  placeholder="email@exemplo.com"
                  aria-invalid={validationErrors.some((e) => e.ref === refEmail)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className="required">Telefone</label>
                <input
                  ref={refTelefone}
                  type="text"
                  inputMode="tel"
                  value={telefone}
                  onChange={(e) => setTelefone(maskPhone(e.target.value))}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                  aria-invalid={validationErrors.some((e) => e.ref === refTelefone)}
                />
              </div>
              <div className={styles.formGroup}>
                <div className={styles.toggleContainer}>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={styles.toggleLabel}>Número de WhatsApp</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vínculo */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <LinkIcon className={styles.cardHeaderIcon} />
            Vínculo
          </div>
          <div className={styles.cardBody}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <Autocomplete
                  size="small"
                  selectOnFocus={false}
                  disableClearable
                  disabled={isEdit}
                  options={[{ value: "parceiro", label: "Parceiro" }, { value: "associado", label: "Associado" }]}
                  getOptionLabel={(opt) => opt?.label ?? ""}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={[{ value: "parceiro", label: "Parceiro" }, { value: "associado", label: "Associado" }].find((o) => o.value === tipoVinculo) ?? null}
                  onChange={(_, newVal) => {
                    setTipoVinculo(newVal?.value ?? "");
                    setAssociadoVinculado("");
                    if (newVal?.value !== "parceiro") setResponsavelComercial("");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo"
                      required
                      inputRef={refTipoVinculo}
                      error={validationErrors.some((e) => e.ref === refTipoVinculo)}
                    />
                  )}
                />
              </div>
              {tipoVinculo === "parceiro" && (
                <div className={styles.formGroup}>
                  <Autocomplete
                    size="small"
                    selectOnFocus={false}
                    options={associadosOpcoes}
                    getOptionLabel={(opt) => opt?.label ?? ""}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    value={associadosOpcoes.find((o) => o.value === associadoVinculado) ?? null}
                    onChange={(_, newVal) => {
                      setAssociadoVinculado(newVal?.value ?? "");
                      setResponsavelComercial("");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Associado Vinculado"
                        required
                        placeholder="Selecione o associado..."
                        inputRef={refAssociado}
                        error={validationErrors.some((e) => e.ref === refAssociado)}
                      />
                    )}
                  />
                </div>
              )}
              <div className={`${styles.formGroup} ${tipoVinculo === "associado" ? styles.formGroupSpan3 : styles.formGroupSpan2}`}>
                <Autocomplete
                  size="small"
                  selectOnFocus={false}
                  disabled={responsavelDisabled}
                  options={responsavelOptions}
                  getOptionLabel={(opt) => opt?.label ?? ""}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={responsavelOptions.find((o) => o.value === responsavelComercial) ?? null}
                  onChange={(_, newVal) => setResponsavelComercial(newVal?.value ?? "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Responsável Comercial"
                      required
                      placeholder={responsavelPlaceholder}
                      inputRef={refResponsavel}
                      error={validationErrors.some((e) => e.ref === refResponsavel)}
                    />
                  )}
                />
                {responsavelHelperText && (
                  <span className={styles.formHelper}>{responsavelHelperText}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dados Complementares CPF */}
        {!docTypeCnpj && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <PersonIcon className={styles.cardHeaderIcon} />
              Dados Complementares
            </div>
            <div className={styles.cardBody}>
              <div className={styles.infoBox}>
                <InfoIcon className={styles.infoBoxIcon} />
                <div>
                  <div className={styles.infoBoxTitle}>Pessoa Física</div>
                  <div className={styles.infoBoxText}>Informações adicionais para cadastro de pessoa física.</div>
                </div>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>RG</label>
                  <input type="text" value={rg} onChange={(e) => setRg(maskRg(e.target.value))} placeholder="00.000.000-0" maxLength={12} />
                </div>
                <div className={styles.formGroup}>
                  <div className={styles.toggleContainer}>
                    <label className={styles.toggleSwitch}>
                      <input type="checkbox" checked={estrangeiro} onChange={(e) => setEstrangeiro(e.target.checked)} />
                      <span className={styles.toggleSlider} />
                    </label>
                    <span className={styles.toggleLabel}>Estrangeiro</span>
                  </div>
                </div>
                <div className={styles.formGroup}>
                  {!estrangeiro ? (
                    <>
                      <label>Passaporte</label>
                      <input type="text" value={passaporte} onChange={(e) => setPassaporte(sanitizePassaporteRne(e.target.value))} placeholder="Número do passaporte" maxLength={20} />
                    </>
                  ) : (
                    <>
                      <label>RNE</label>
                      <input type="text" value={rne} onChange={(e) => setRne(sanitizePassaporteRne(e.target.value))} placeholder="Registro Nacional de Estrangeiro" maxLength={20} />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dados Complementares CNPJ */}
        {docTypeCnpj && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <BusinessIcon className={styles.cardHeaderIcon} />
              Dados Complementares
            </div>
            <div className={styles.cardBody}>
              <div className={styles.infoBox}>
                <InfoIcon className={styles.infoBoxIcon} />
                <div>
                  <div className={styles.infoBoxTitle}>Pessoa Jurídica</div>
                  <div className={styles.infoBoxText}>Informações adicionais e documentos para cadastro de pessoa jurídica.</div>
                </div>
              </div>
              <div className={`${styles.sectionTitle} ${styles.sectionTitleFirst}`}>
                <GroupIcon className={styles.sectionTitleIcon} />
                Quadro de Sócios
              </div>
              <div className={styles.tableWrapper}>
                <table className={styles.sociosTable}>
                  <thead>
                    <tr>
                      <th style={{ width: "140px" }}>CPF</th>
                      <th style={{ width: "180px" }}>Nome Completo</th>
                      <th style={{ width: "180px" }}>E-mail</th>
                      <th style={{ width: "130px" }}>Telefone</th>
                      <th style={{ width: "110px", textAlign: "center" }}>Resp. Legal</th>
                      <th style={{ width: "130px", textAlign: "center" }}>Resp. Financeiro</th>
                      <th style={{ width: "80px", textAlign: "center" }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {socios.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <input
                            placeholder="000.000.000-00"
                            value={row.cpf}
                            onChange={(e) => updateSocio(idx, "cpf", maskCpf(e.target.value))}
                          />
                        </td>
                        <td><input placeholder="Nome do sócio" value={row.nome} onChange={(e) => updateSocio(idx, "nome", sanitizeNome(e.target.value))} maxLength={200} /></td>
                        <td><input type="email" placeholder="email@exemplo.com" value={row.email} onChange={(e) => updateSocio(idx, "email", sanitizeEmail(e.target.value))} maxLength={120} /></td>
                        <td>
                          <input
                            placeholder="(00) 00000-0000"
                            value={row.telefone}
                            onChange={(e) => updateSocio(idx, "telefone", maskPhone(e.target.value))}
                          />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <label className={styles.toggleSwitch} style={{ display: "inline-block" }}>
                            <input type="checkbox" checked={row.respLegal} onChange={(e) => updateSocio(idx, "respLegal", e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <label className={styles.toggleSwitch} style={{ display: "inline-block" }}>
                            <input type="checkbox" checked={row.respFinanceiro} onChange={(e) => updateSocio(idx, "respFinanceiro", e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button type="button" className={styles.btnRemoveRow} onClick={() => removeSocio(idx)}>
                            Remover
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" className={styles.btnAddRow} onClick={addSocio}>
                <AddIcon sx={{ fontSize: 16 }} />
                Adicionar Sócio
              </button>
            </div>
          </div>
        )}

        {/* Dados Financeiros */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <AccountBalanceIcon className={styles.cardHeaderIcon} />
            Dados Financeiros
          </div>
          <div className={styles.cardBody}>
            <div className={`${styles.sectionTitle} ${styles.sectionTitleFirst}`}>
              <PersonIcon className={styles.sectionTitleIcon} />
              Responsável Financeiro
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>CPF do Responsável</label>
                <input type="text" inputMode="numeric" value={respFinanceiroCpf} onChange={(e) => setRespFinanceiroCpf(maskCpf(e.target.value))} placeholder="000.000.000-00" maxLength={14} />
              </div>
              <div className={styles.formGroup}>
                <label>Nome</label>
                <input type="text" value={respFinanceiroNome} onChange={(e) => setRespFinanceiroNome(sanitizeNome(e.target.value))} placeholder="Nome do responsável" maxLength={200} />
              </div>
              <div className={styles.formGroup}>
                <label>E-mail</label>
                <input type="email" inputMode="email" value={respFinanceiroEmail} onChange={(e) => setRespFinanceiroEmail(sanitizeEmail(e.target.value))} placeholder="email@exemplo.com" maxLength={120} />
              </div>
              <div className={styles.formGroup}>
                <label>Telefone</label>
                <input type="text" inputMode="tel" value={respFinanceiroTelefone} onChange={(e) => setRespFinanceiroTelefone(maskPhone(e.target.value))} placeholder="(00) 00000-0000" maxLength={15} />
              </div>
            </div>
            <div className={styles.sectionTitle}>
              <AccountBalanceIcon className={styles.sectionTitleIcon} />
              Dados Bancários
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>Banco</label>
                <Autocomplete
                  size="small"
                  selectOnFocus={false}
                  options={BANCOS_OPTIONS}
                  getOptionLabel={(opt) => opt?.label ?? ""}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={BANCOS_OPTIONS.find((b) => b.value === banco) ?? null}
                  onChange={(_, newVal) => setBanco(newVal?.value ?? "")}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Selecione o banco..." />
                  )}
                />
              </div>
              {banco === "other" && (
                <div className={styles.formGroup}>
                  <label>Nome do Banco</label>
                  <input
                    type="text"
                    value={bancoOutro}
                    onChange={(e) => setBancoOutro(e.target.value)}
                    placeholder="Digite o nome do banco"
                    maxLength={100}
                  />
                </div>
              )}
              <div className={styles.formGroup}>
                <label>Agência</label>
                <input type="text" inputMode="numeric" value={agencia} onChange={(e) => setAgencia(sanitizeAgencia(e.target.value))} placeholder="Somente números" maxLength={6} />
              </div>
              <div className={styles.formGroup}>
                <label>Conta Corrente</label>
                <input type="text" inputMode="numeric" value={conta} onChange={(e) => setConta(sanitizeConta(e.target.value))} placeholder="00000-0 (números e hífen)" maxLength={14} />
              </div>
              <div className={styles.formGroup}>
                <label>Chave PIX</label>
                <input type="text" value={pix} onChange={(e) => setPix(sanitizePix(e.target.value))} placeholder="E-mail, CPF, CNPJ ou chave aleatória" maxLength={100} />
              </div>
            </div>
          </div>
        </div>
        </div>
      </main>

      <div className={styles.actionFooter}>
        <div className={styles.footerInfo}>{loadingForm ? "Carregando..." : ""}</div>
        <div className={styles.footerActions}>
          <button type="button" className={`${styles.btn} ${styles.btnOutline}`} onClick={() => navigate("/acessos/parceiros-associados")} disabled={saving}>
            <CloseIcon sx={{ fontSize: 18 }} />
            Cancelar
          </button>
          {superAdmin && (
            <button type="button" className={`${styles.btn} ${styles.btnOutline}`} onClick={fillMock} title="Preenche todos os campos com dados de teste" disabled={saving}>
              <ScienceIcon sx={{ fontSize: 18 }} />
              Preencher com dados de teste
            </button>
          )}
          <button type="button" className={`${styles.btn} ${styles.btnSuccess}`} onClick={handleSave} disabled={saving || loadingForm}>
            <SaveIcon sx={{ fontSize: 18 }} />
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
      </div>

      {successModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setSuccessModalOpen(false)} role="presentation">
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} role="dialog">
            <div className={styles.modalHeader}>
              <CheckCircleIcon className={styles.modalSuccessIcon} />
              <h3 className={styles.modalTitle}>Cadastro Salvo com Sucesso!</h3>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalMessage}>
                O cadastro de <span className={styles.modalHighlight}>{savedName}</span> foi salvo com sucesso.
              </p>
              <p className={styles.modalQuestion}>O que deseja fazer agora?</p>
              <div className={styles.modalOptions}>
                <button type="button" className={`${styles.modalOptionBtn} ${styles.modalOptionBtnPrimary}`} onClick={() => { setSuccessModalOpen(false); navigate("/acessos/parceiros-associados"); }}>
                  <ListIcon className={styles.modalOptionIcon} />
                  <div>
                    <div className={styles.modalOptionTitle}>Voltar para Lista</div>
                    <div className={styles.modalOptionDesc}>Retornar para a listagem de Parceiros e Associados</div>
                  </div>
                </button>
                <button type="button" className={styles.modalOptionBtn} onClick={() => { resetForm(); setSuccessModalOpen(false); }}>
                  <AddCircleIcon className={styles.modalOptionIcon} />
                  <div>
                    <div className={styles.modalOptionTitle}>Novo Cadastro</div>
                    <div className={styles.modalOptionDesc}>Criar um novo cadastro de Parceiro ou Associado (formulário será limpo)</div>
                  </div>
                </button>
                {docTypeCnpj && (
                  <button type="button" className={styles.modalOptionBtn} onClick={() => { setSuccessModalOpen(false); navigate(`/cadastros/empresas?cnpj=${documento.replace(/\D/g, "")}`); }}>
                    <BusinessIcon className={styles.modalOptionIcon} />
                    <div>
                      <div className={styles.modalOptionTitle}>Cadastrar Empresa</div>
                      <div className={styles.modalOptionDesc}>Ir para o cadastro de empresa com este CNPJ</div>
                    </div>
                  </button>
                )}
                {!docTypeCnpj && (
                  <button type="button" className={styles.modalOptionBtn} onClick={() => { setSuccessModalOpen(false); navigate(`/cadastros/usuarios?cpf=${documento.replace(/\D/g, "")}`); }}>
                    <PersonAddIcon className={styles.modalOptionIcon} />
                    <div>
                      <div className={styles.modalOptionTitle}>Cadastrar Usuário</div>
                      <div className={styles.modalOptionDesc}>Ir para o cadastro de usuário com este CPF</div>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
